import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import {
  HtmlOverlayAnchor,
  HtmlOverlayPriority,
} from 'features/assetViz/utils/helpers'
import React from 'react'
import styled, { RuleSet } from 'styled-components'
import { Vector3 } from 'three'

import { HtmlOverlay3D } from './HtmlOverlay3D'
import { getCssRulesForAnchor } from './Ruler3D'

const StyledLabel = styled.div<{
  $cssRules: RuleSet<object>
}>`
  display: flex;
  min-width: 2rem;
  line-height: 1;
  user-select: none;
  pointer-events: none;
  ${({ $cssRules }) => $cssRules};
`

/**
 * A component used to line up clock bearing positions in 3D space
 * @param anchor              Optional. The anchor point for ruler ticks
 * @param includeStart        Boolean to include a "0:00", visually displayed as
 *                            12:00.
 * @param intervalSize        Optional. Interval size in terms of hours.
 *                            Floored if passed in as a float.
 * @param getTickPosition     A function for calculating 3D tick position
 */
export const ClockBearing3D: React.FC<{
  anchor?: HtmlOverlayAnchor
  includeStart: boolean
  intervalSize?: number
  getTickPosition: (coord: number) => Vector3
}> = ({ anchor, includeStart, intervalSize, getTickPosition }) => {
  //----------------------------------------------------------------------------

  const { showRulers } = useAssetVizInternal()
  const { componentModel } = useSurfaceProps()

  const showThisClockBearing = React.useMemo(() => {
    if (showRulers === false || showRulers === true) return showRulers
    return componentModel && showRulers(componentModel)
  }, [componentModel, showRulers])

  return !showThisClockBearing ? null : (
    <ClockBearingInner
      anchor={anchor ?? 'center'}
      includeStart={includeStart}
      intervalSize={Math.floor(intervalSize ?? 3)}
      getTickPosition={getTickPosition}
    />
  )
}

export const ClockBearingInner: React.FC<{
  anchor: HtmlOverlayAnchor
  includeStart: boolean
  intervalSize: number
  getTickPosition: (coord: number) => Vector3
}> = ({ anchor, includeStart, intervalSize, getTickPosition }) => {
  //----------------------------------------------------------------------------

  const tickMarks = React.useMemo(() => {
    const result = new Array<number>()
    const clockEnd = 12
    const clockStart = includeStart ? 0 : intervalSize

    for (let i = clockStart; i <= clockEnd; i += intervalSize) result.push(i)

    if (result.at(-1) !== clockEnd) result.push(clockEnd)

    return result.map((coord, i) => {
      const position = getTickPosition(coord)
      const isFirstOrLast = i === 0 || i === result.length - 1
      const priority: HtmlOverlayPriority =
        isFirstOrLast || i % 10 === 0
          ? HtmlOverlayPriority.RulerTickMedium
          : i % 5 === 0
            ? HtmlOverlayPriority.RulerTickMedium
            : HtmlOverlayPriority.RulerTickLow
      const maxPixelOverlap = isFirstOrLast ? 8 : 0
      const cssRules = getCssRulesForAnchor(anchor)
      return {
        key: coord.toString(),
        coord: coord > 0 ? `${coord}:00` : '12:00',
        x: position.x,
        y: position.y,
        z: position.z,
        priority,
        cssRules,
        maxPixelOverlap,
      }
    })
  }, [anchor, getTickPosition, includeStart, intervalSize])

  //----------------------------------------------------------------------------

  return (
    <>
      {tickMarks.map(
        ({ key, coord, x, y, z, priority, cssRules, maxPixelOverlap }) => {
          return (
            <HtmlOverlay3D
              key={key}
              x={x}
              y={y}
              z={z}
              anchor={anchor}
              priority={priority}
              maxPixelOverlap={maxPixelOverlap}>
              <StyledLabel $cssRules={cssRules}>{coord}</StyledLabel>
            </HtmlOverlay3D>
          )
        }
      )}
    </>
  )
}
